import React, { useMemo, useRef, useState } from "react";
import {
  ClipboardCheck,
  Warehouse,
  Truck,
  Ship,
  Plane,
  MapPinned,
  PackageCheck,
  Link2,
  Check,
  RotateCcw,
  MessageCircle,
  Ban,
} from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { localCity } from "@/lib/storyTranslations";
import { formatTracking } from "@/lib/trackApi";
import { gsap, useGsap } from "@/lib/motion";
import TrackDetails from "./TrackDetails";

const INTL_LOCALE = { ar: "ar-u-nu-latn", en: "en-GB", tr: "tr-TR", "zh-CN": "zh-CN" };
const MODE_ICON = { road: Truck, sea: Ship, air: Plane };
const WHATSAPP = "905521627900";
const SIGNAL_70 = "oklch(0.9 0.19 122 / 0.7)";

function useFormatters(lang) {
  return useMemo(() => {
    const loc = INTL_LOCALE[lang] || "en-GB";
    const dateTime = new Intl.DateTimeFormat(loc, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    const dateOnly = new Intl.DateTimeFormat(loc, { day: "numeric", month: "long", year: "numeric" });
    const rel = new Intl.RelativeTimeFormat(loc, { numeric: "auto" });
    const relative = (iso) => {
      const diff = (Date.parse(iso) - Date.now()) / 1000;
      const abs = Math.abs(diff);
      if (abs < 3600) return rel.format(Math.round(diff / 60), "minute");
      if (abs < 86400) return rel.format(Math.round(diff / 3600), "hour");
      return rel.format(Math.round(diff / 86400), "day");
    };
    return {
      dateTime: (iso) => (iso ? dateTime.format(new Date(iso)) : null),
      // ETA / departure arrive as plain dates; pin to noon UTC so no timezone shifts the day.
      day: (d) => (d ? dateOnly.format(new Date(`${d}T12:00:00Z`)) : null),
      relative: (iso) => (iso ? relative(iso) : null),
    };
  }, [lang]);
}

export default function TrackResult({ shipment, onReset }) {
  const { t, lang } = useLang();
  const tr = t.track;
  const r = tr.result;
  const isRtl = lang === "ar";
  const fmt = useFormatters(lang);
  const root = useRef(null);
  const [copied, setCopied] = useState(false);

  const cancelled = shipment.state === "cancelled";
  const stage = cancelled ? 0 : shipment.stage ?? 0;
  const ModeIcon = MODE_ICON[shipment.mode] || Truck;
  const pct = (stage / 4) * 100;
  const side = isRtl ? "right" : "left";
  const origin = localCity(shipment.originCity, t.cities) || r.unknownCity;
  const destination = localCity(shipment.destinationCity, t.cities) || r.unknownCity;
  const stageIcons = [ClipboardCheck, Warehouse, ModeIcon, MapPinned, PackageCheck];

  const total = shipment.parcelsTotal || 0;
  const arrived = (shipment.parcelsByStage?.["3"] || 0) + (shipment.parcelsByStage?.["4"] || 0);

  useGsap(
    () => {
      const tl = gsap.timeline();
      tl.from(".tr-card", { y: 32, opacity: 0, duration: 0.9 })
        .fromTo(".tr-fill", { scaleX: 0 }, { scaleX: 1, duration: 1.4, ease: "expo.inOut" }, 0.25)
        .fromTo(".tr-marker", { [side]: "0%" }, { [side]: `${pct}%`, duration: 1.4, ease: "expo.inOut" }, 0.25)
        .from(".tr-node", { scale: 0, duration: 0.5, stagger: 0.12, ease: "power3.out" }, 0.3)
        .from(".tr-row", { opacity: 0, y: 12, duration: 0.6, stagger: 0.07 }, 0.6)
        .from(".tr-fact", { opacity: 0, y: 12, duration: 0.6, stagger: 0.05 }, 0.8);
    },
    [shipment.trackingNumber, lang],
    root,
  );

  const copyLink = async () => {
    const url = `${window.location.origin}/track/${shipment.trackingNumber}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      window.prompt(r.copyLink, url);
    }
  };

  const waText = encodeURIComponent(`${r.shipment} ${shipment.trackingNumber}`);
  const centerX = isRtl ? "translate-x-1/2" : "-translate-x-1/2";

  return (
    <div ref={root} className="mt-10" data-testid="track-result">
      <article className="tr-card relative rounded-[1.75rem] bg-[oklch(0.215_0.03_140)] border border-white/10 overflow-hidden">
        {/* Waybill header */}
        <header className="px-5 sm:px-8 pt-6 sm:pt-8 pb-6 flex flex-wrap items-start justify-between gap-5">
          <div>
            <div className="text-sm text-[var(--on-dark-muted)]">{r.shipment}</div>
            <div className="mt-1 type-num type-wide text-2xl sm:text-3xl font-extrabold tracking-tight text-[var(--on-dark)]" dir="ltr">
              {formatTracking(shipment.trackingNumber)}
            </div>
          </div>
          {cancelled ? (
            <div className="flex items-center gap-2.5 rounded-full bg-[oklch(0.35_0.09_25/0.35)] border border-[oklch(0.7_0.15_25/0.4)] px-4 py-2 text-[oklch(0.88_0.08_25)] font-semibold">
              <Ban className="w-4 h-4" aria-hidden /> {r.cancelled}
            </div>
          ) : (
            <div className="text-end">
              <div className="text-sm text-[var(--on-dark-muted)]">{r.current}</div>
              <div className="mt-1 flex items-center gap-2.5 justify-end">
                <span className="relative flex h-2.5 w-2.5" aria-hidden>
                  {stage < 4 && <span className="absolute inset-0 rounded-full bg-[var(--signal)] opacity-60 motion-safe:animate-ping" />}
                  <span className="relative h-2.5 w-2.5 rounded-full bg-[var(--signal)]" />
                </span>
                <span className="text-lg sm:text-xl font-bold text-[var(--on-dark)]" aria-live="polite">
                  {tr.details[shipment.detail] || tr.stages[stage]}
                </span>
              </div>
            </div>
          )}
        </header>

        {/* Route: origin → destination with the parcel riding to its current stage */}
        <div className="px-5 sm:px-8 pb-8">
          <div className="flex items-end justify-between gap-4 mb-5">
            <div>
              <div className="text-xs text-[var(--on-dark-muted)]">{r.from}</div>
              <div className="text-xl sm:text-2xl font-extrabold text-[var(--on-dark)]">{origin}</div>
            </div>
            <div className="text-end">
              <div className="text-xs text-[var(--on-dark-muted)]">{r.to}</div>
              <div className="text-xl sm:text-2xl font-extrabold text-[var(--on-dark)]">{destination}</div>
            </div>
          </div>

          <div className="relative h-14 mx-5" aria-hidden>
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[3px] rounded-full bg-[repeating-linear-gradient(90deg,oklch(1_0_0/0.22)_0_8px,transparent_8px_14px)]" />
            {!cancelled && (
              <div
                className="tr-fill absolute top-1/2 -mt-[1.5px] h-[3px] rounded-full bg-[var(--signal)]"
                style={{ insetInlineStart: 0, width: `${pct}%`, transformOrigin: side }}
              />
            )}
            {[0, 1, 2, 3, 4].map((i) => (
              <span
                key={i}
                className={`absolute top-1/2 -translate-y-1/2 ${centerX}`}
                style={{ insetInlineStart: `${i * 25}%` }}
              >
                <span
                  className={`tr-node block w-3.5 h-3.5 rounded-full border-2 ${
                    !cancelled && i <= stage ? "bg-[var(--signal)] border-[var(--signal)]" : "bg-[oklch(0.215_0.03_140)] border-white/30"
                  }`}
                />
              </span>
            ))}
            {!cancelled && (
              <div className="tr-marker absolute top-1/2" style={{ [side]: `${pct}%` }}>
                <div className={`-translate-y-1/2 ${centerX}`}>
                  <div className="w-11 h-11 rounded-xl bg-[var(--signal)] text-[var(--signal-ink)] grid place-items-center shadow-[0_0_0_6px_oklch(0.9_0.19_122/0.18),0_10px_30px_-8px_oklch(0.9_0.19_122/0.6)]">
                    <ModeIcon className={`w-5 h-5 ${isRtl ? "rtl-flip" : ""}`} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tear line */}
        <div className="relative h-6" aria-hidden>
          <div className="absolute inset-x-6 top-1/2 border-t-2 border-dashed border-white/10" />
          <span className="absolute -start-3 top-0 w-6 h-6 rounded-full bg-[var(--track-bg)]" />
          <span className="absolute -end-3 top-0 w-6 h-6 rounded-full bg-[var(--track-bg)]" />
        </div>

        <div className="grid lg:grid-cols-12 gap-8 px-5 sm:px-8 pt-4 pb-8">
          {/* Stage timeline */}
          <ol className="lg:col-span-7 relative">
            {tr.stages.map((label, i) => {
              const Icon = stageIcons[i];
              const reached = !cancelled && i <= stage;
              const current = !cancelled && i === stage;
              const when = shipment.stageTimes?.[String(i)];
              return (
                <li key={label} className="tr-row relative flex gap-4 pb-6 last:pb-0">
                  {i < 4 && (
                    <span
                      aria-hidden
                      className="absolute top-11 bottom-1 start-[21px] w-[2px]"
                      style={{ background: !cancelled && i < stage ? SIGNAL_70 : "oklch(1 0 0 / 0.1)" }}
                    />
                  )}
                  <span
                    className={`relative z-raised shrink-0 w-11 h-11 rounded-full grid place-items-center ${
                      current
                        ? "bg-[var(--signal)] text-[var(--signal-ink)] ring-4 ring-[oklch(0.9_0.19_122/0.2)]"
                        : reached
                        ? "bg-[oklch(0.37_0.078_138)] text-[var(--on-dark)]"
                        : "bg-white/5 text-white/40 border border-dashed border-white/20"
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${i === 2 && isRtl ? "rtl-flip" : ""}`} aria-hidden />
                  </span>
                  <div className="pt-1.5 min-w-0">
                    <div className={`font-bold ${reached ? "text-[var(--on-dark)]" : "text-white/50"}`}>
                      {label}
                      <span className="sr-only">{current ? " (current)" : reached ? " (done)" : ""}</span>
                    </div>
                    {current && shipment.detail && tr.details[shipment.detail] && (
                      <div className="mt-0.5 text-sm text-[var(--signal)]">{tr.details[shipment.detail]}</div>
                    )}
                    {when && reached && (
                      <div className="mt-0.5 text-sm text-[var(--on-dark-muted)] type-num">{fmt.dateTime(when)}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>

          {/* Facts */}
          <dl className="lg:col-span-5 grid grid-cols-2 gap-px rounded-2xl overflow-hidden bg-white/10 self-start">
            {[
              { k: r.mode, v: shipment.mode ? `${tr.modes[shipment.mode]}${shipment.scope === "domestic" ? ` · ${r.domestic}` : ""}` : r.pending },
              { k: r.eta, v: fmt.day(shipment.eta) || r.pending },
              { k: r.departed, v: fmt.day(shipment.departedOn) || r.pending },
              { k: r.parcels, v: total ? r.parcelsOf(arrived, total) : r.noParcels, bar: total ? arrived / total : null },
              { k: r.lastUpdate, v: fmt.relative(shipment.lastUpdate) || r.pending, wide: true },
            ].map((f) => (
              <div key={f.k} className={`tr-fact bg-[oklch(0.24_0.032_140)] p-4 sm:p-5 ${f.wide ? "col-span-2" : ""}`}>
                <dt className="text-xs text-[var(--on-dark-muted)]">{f.k}</dt>
                <dd className="mt-1 font-bold text-[var(--on-dark)] type-num">{f.v}</dd>
                {typeof f.bar === "number" && (
                  <div className="mt-2.5 h-1.5 rounded-full bg-white/10 overflow-hidden" aria-hidden>
                    <div className="h-full rounded-full bg-[var(--signal)]" style={{ width: `${Math.round(f.bar * 100)}%` }} />
                  </div>
                )}
              </div>
            ))}
          </dl>
        </div>

        <TrackDetails shipment={shipment} />

        {cancelled && <p className="px-5 sm:px-8 pb-6 -mt-2 text-sm text-[var(--on-dark-muted)]">{r.cancelledNote}</p>}

        {/* Actions */}
        <footer className="flex flex-wrap items-center justify-between gap-3 px-5 sm:px-8 py-5 border-t border-white/10 bg-black/10">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={copyLink}
              className="h-10 px-4 rounded-full inline-flex items-center gap-2 text-sm font-semibold border border-white/15 text-[var(--on-dark)] hover:border-[var(--signal)] hover:text-[var(--signal)] transition-colors"
            >
              {copied ? <Check className="w-4 h-4" aria-hidden /> : <Link2 className="w-4 h-4" aria-hidden />}
              <span aria-live="polite">{copied ? r.copied : r.copyLink}</span>
            </button>
            <button
              type="button"
              onClick={onReset}
              className="h-10 px-4 rounded-full inline-flex items-center gap-2 text-sm font-semibold border border-white/15 text-[var(--on-dark)] hover:border-[var(--signal)] hover:text-[var(--signal)] transition-colors"
            >
              <RotateCcw className="w-4 h-4" aria-hidden /> {r.trackAnother}
            </button>
          </div>
          <a
            href={`https://wa.me/${WHATSAPP}?text=${waText}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--on-dark-muted)] hover:text-[var(--signal)] transition-colors"
          >
            <MessageCircle className="w-4 h-4" aria-hidden />
            {r.help} <span className="underline underline-offset-4">{r.whatsapp}</span>
          </a>
        </footer>
      </article>
    </div>
  );
}
